// @spec INC-STR-040, NIS2-STR-023, INC-STR-006
'use client';

import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  IncidentAttachmentsList,
  IncidentDetailHeader,
  IncidentTimeline,
  useIncident,
} from '@/features/incidents';
import { IncidentEditableForm } from '@/features/incidents/components/IncidentEditableForm';
import { NIS2FlagSection } from '@/features/incidents/nis2';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/incidents/$incidentId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.incidentDetails'),
  }),
  component: IncidentDetailPage,
});

/**
 * Incident detail page showing full incident information.
 *
 * Displays:
 * - Header with incident number, title, and status badges
 * - Details section with type, category, tags, and dates
 * - People section with reporter, owner, and assigned user
 * - Description section
 * - Attachments list
 * - Timeline of incident events
 */
function IncidentDetailPage() {
  const { t } = useTranslation('incidents');
  const navigate = useNavigate();

  const params = useParams({ from: '/_authenticated/incidents/$incidentId' });
  const incidentId = params.incidentId;

  const { organizations, isLoading: orgsLoading } = useOrganizations();

  const activeOrg = organizations.find((org) => org.isActive);

  const {
    incident,
    isLoading: incidentLoading,
    isError,
    error,
    refetch,
  } = useIncident({
    incidentId,
    enabled: Boolean(activeOrg),
  });

  const handleBack = () => {
    void navigate({ to: '/incidents' });
  };

  // Loading state
  if (orgsLoading || incidentLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <Skeleton className="h-8 w-32" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-8 w-96" />
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
          <Skeleton className="h-32" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  // No active organization
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('detailPage.noOrgSelected')}</AlertTitle>
            <AlertDescription>{t('detailPage.noOrgSelectedDesc')}</AlertDescription>
          </Alert>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/settings/organizations' })}
          >
            {t('detailPage.goToOrganizations')}
          </Button>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    const is404 = (error as { status?: number }).status === 404;
    const is403 = (error as { status?: number }).status === 403;

    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {is404
                ? t('detailPage.notFound')
                : is403
                  ? t('detailPage.accessDenied')
                  : t('detailPage.error')}
            </AlertTitle>
            <AlertDescription>
              {is404
                ? t('detailPage.notFoundDesc')
                : is403
                  ? t('detailPage.accessDeniedDesc')
                  : t('detailPage.errorDesc')}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/incidents">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('detailPage.backToIncidents')}
              </Link>
            </Button>
            {!is404 && !is403 && (
              <Button variant="outline" onClick={() => void refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('detailPage.retry')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No incident data
  if (!incident) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('detailPage.notFound')}</AlertTitle>
            <AlertDescription>{t('detailPage.notFoundSimple')}</AlertDescription>
          </Alert>
          <Button variant="outline" asChild>
            <Link to="/incidents">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('detailPage.backToIncidents')}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <IncidentDetailHeader
          incidentNumber={incident.incidentNumber}
          title={incident.title}
          status={incident.status}
          severity={incident.severity}
          priority={incident.priority}
          onBack={handleBack}
        />

        <IncidentEditableForm
          incident={incident}
          onSaved={() => {
            void refetch();
          }}
        />

        {/* NIS2 Compliance Section */}
        <NIS2FlagSection incidentId={incidentId} incidentDetectedAt={incident.discoveredAt} />

        {/* Attachments */}
        <IncidentAttachmentsList attachments={incident.attachments} />

        {/* Timeline */}
        <IncidentTimeline entries={incident.timeline} />
      </div>
    </div>
  );
}
